/**
 * Pure presentation component for a menu row button with optional icons
 *
 * Displays an icon, label, optional description, and optional right icon
 * in a horizontal layout. Supports dark mode via isDark prop and hover
 * states on web.
 * @module components/RowButton/RowButtonView
 */

import { PressableProps } from "react-native";
import React, { memo } from "react";

import { Pressable } from "@/components/atoms/Pressable";
import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";
import { ifWeb } from "@/utils/platform";

/** Semantic border tone for a row's top divider */
type RowButtonBorderTone = "default" | "subtle" | "strong";

/**
 * Closed map from border tone → semantic outline token class.
 * Replaces caller-passed border classNames (RFC §4: no className above atoms).
 */
const TOP_BORDER_CLASS: Record<RowButtonBorderTone, string> = {
  default: "border-t border-outline-default",
  subtle: "border-t border-outline-subtle",
  strong: "border-t border-outline-strong",
};

/**
 * Props for RowButtonView component
 */
interface RowButtonViewProps extends PressableProps {
  label: string;
  description?: string;
  leftIcon?: React.ElementType;
  rightIcon?: React.ElementType;
  isDark?: boolean;
  /** Renders a top divider; tone selects the semantic outline token (default: no border) */
  topBorderTone?: RowButtonBorderTone;
  /** Indents the row content (nested sub-rows) */
  indented?: boolean;
  /** Renders the label in the error tone (destructive action) */
  destructive?: boolean;
}

/**
 * SE-5550: auto-inverting semantic content tokens, not isDark-forked raw-palette
 * shades. The typography scale INVERTS by theme, so the old
 * `isDark ? text-typography-100 : text-typography-700` double-inverted —
 * typography-100 in dark = 64/64/64, dark-on-dark, which reads as "disabled".
 * `content-secondary` (= typography-700) auto-inverts: light 82/82/82, the same
 * as before, dark 219/219/220, legible. `content-muted` (its own var since
 * SE-8550: 115/115/115 light, 163/163/163 dark) keeps the muted description
 * tone in both themes.
 */
const ICON_COLOR = "text-content-secondary";
/** Description tone, muted in both themes. */
const DESC_COLOR = "text-content-muted";

/**
 * Label tone — destructive rows read in the error colour.
 * @param destructive - Whether the row performs a destructive action.
 * @returns The text colour class.
 */
const textColorFor = (destructive: boolean | undefined): string =>
  destructive === true ? "text-status-error" : "text-content-secondary";

/**
 * Hover fill. Dark surfaces lift, light surfaces raise.
 * @param isDark - Whether the row sits on a dark surface.
 * @returns The hover class.
 */
const hoverBgFor = (isDark: boolean | undefined): string =>
  isDark === true
    ? "web:hover:bg-surface-inverse/5"
    : "web:hover:bg-surface-raised";

/**
 * Renders a pressable row with optional left/right icons, label, and description.
 * Adapts to dark mode and provides hover feedback on web.
 * @param root0 - Component props
 * @param root0.label - Button label text
 * @param root0.description - Optional description text below the label
 * @param root0.leftIcon - Optional icon component to display on the left
 * @param root0.rightIcon - Optional icon component to display on the right
 * @param root0.isDark - Whether dark mode is active
 * @param root0.topBorderTone - When set, renders a top divider in the given semantic outline tone
 * @param root0.indented - Whether to indent the row content (nested sub-rows)
 * @param root0.destructive - Whether to render the label in the error tone
 */
const RowButtonView = React.forwardRef<
  React.ElementRef<typeof Pressable>,
  RowButtonViewProps
>(
  (
    {
      label,
      description,
      leftIcon,
      rightIcon,
      isDark,
      topBorderTone,
      indented,
      destructive,
      ...pressableProps
    },
    ref
  ) => (
    <Pressable
      {...pressableProps}
      ref={ref}
      className={`flex-row items-center gap-3 rounded-sm ${ifWeb("px-3 py-3", "px-4 py-3")} ${indented === true ? "pl-8" : ""} ${hoverBgFor(isDark)} active:opacity-80 ${topBorderTone ? TOP_BORDER_CLASS[topBorderTone] : ""}`}
    >
      {!!leftIcon && (
        <Box className="w-5 items-center">
          <Icon as={leftIcon} size="sm" className={ICON_COLOR} />
        </Box>
      )}
      <Stack className="flex-1">
        <Text
          variant="unstyled"
          className={`font-medium ${ifWeb("text-title-sm", "text-title")} ${textColorFor(destructive)}`}
        >
          {label}
        </Text>
        {!!description && (
          <Text variant="caption" className={DESC_COLOR}>
            {description}
          </Text>
        )}
      </Stack>
      {!!rightIcon && (
        <Box className="w-5 items-center">
          <Icon as={rightIcon} size="sm" className={ICON_COLOR} />
        </Box>
      )}
    </Pressable>
  )
);

RowButtonView.displayName = "RowButtonView";

export default memo(RowButtonView);
